import React, { useState } from 'react';
import { Button } from '../index';
import { RatingStar } from '../Icon';
import Styled from './styled';

export interface MultiSelectItemProps {
    /** Label text to display */
    label: string;
    /** Unique value for the item (used as identifier) */
    value: string;
    /** Determines the button appearance (button, checkbox or radio) defaults to 'checkbox' */
    variant?: 'button' | 'checkbox' | 'radio';
    /** Optional initial selected state */
    selected?: boolean;
    /** Optional initial selected rating value */
    rating?: number;
    /** If true, a rating selector (star rating) appears when the item is selected */
    showRating?: boolean;
    /** How many rating stars to display (defaults to 5) */
    ratingCount?: number;
    /** Whether the item is disabled */
    disabled?: boolean;
}

export interface MultiSelectProps {
    /** Optional title for the multi-select wrapper */
    title?: string;
    /** Optional subtitle */
    subtitle?: string;
    /** Optional submit button label (defaults to 'Submit') */
    submitLabel?: string;
    /** List of selectable items */
    items: MultiSelectItemProps[];
    /** Global disable flag (defaults to False) */
    disabled?: boolean;
    /** If true, items will stretch to fill available width (defaults to False) */
    stretch?: boolean;
    /** If true, a visual indentation and left border is applied (defaults to True) */
    feedback?: boolean;
    /** Callback executed when the user submits */
    onSubmitClick: (values: Record<string, Selection>) => void;
}

export interface Selection {
    selected: boolean;
    rating?: number;
}

/**
 * MultiSelect component – renders a list of selectable items
 *
 * Each item uses the Button component for checkbox (or radio) selection
 * If an item is marked with `showRating` and is selected, a star-rating row is shown below it
 */
export const MultiSelect: React.FC<MultiSelectProps> = ({
    title,
    subtitle,
    submitLabel = 'Submit',
    items,
    disabled = false,
    stretch = false,
    feedback = false,
    onSubmitClick,
}) => {

    const [selections, setSelections] = useState<Record<string, Selection>>(() => {
        const initial: Record<string, Selection> = {};
        items.forEach((item) => {
            initial[item.value] = { selected: !!item.selected, rating: item.rating ?? undefined };
        });
        return initial;
    });

    const handleToggle = (id: string) => {
        setSelections((prev) => {
            const current = prev[id];
            return {
                ...prev,
                [id]: {
                    ...current,
                    selected: !current.selected,
                    // Clear the rating if unselecting the item
                    rating: !current.selected ? current.rating : undefined,
                },
            };
        });
    };

    const handleRatingChange = (id: string, value: number) => {
        setSelections((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                rating: value,
                // Ensure the item stays selected when a rating is picked
                selected: true,
            },
        }));
    };

    const handleSubmit = () => {
        onSubmitClick(selections);
    };

    return (
        <Styled.MultiSelectWrapper>
            {title && <Styled.Title>{title}</Styled.Title>}
            {subtitle && <Styled.Subtitle>{subtitle}</Styled.Subtitle>}
            <Styled.ItemsContainer feedback={feedback} className='multiselect-items'>
                {items.map((item) => {
                    const selection = selections[item.value];
                    return (
                        <Styled.ItemWrapper key={item.value}>
                            {/* Render the Button for selection */}
                            <Styled.Item>
                                <Button
                                    label={item.label}
                                    value={item.value}
                                    variant={item.variant || 'checkbox'}
                                    disabled={item.disabled || disabled}
                                    selected={selection.selected}
                                    onClick={() => handleToggle(item.value)}
                                    stretch={stretch}
                                />
                            </Styled.Item>
                            {/* Render the rating stars if applicable */}
                            {item.showRating && selection.selected && (
                                <Styled.RatingContainer>
                                    {Array.from({ length: item.ratingCount || 5 }).map((_, index) => (
                                        <Styled.RatingButton
                                            key={index}
                                            aria-label={item.value}
                                            tabIndex={0}
                                            role='button'
                                            onClick={() => handleRatingChange(item.value, index + 1)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault(); // Prevent default scrolling for Space key
                                                    handleRatingChange(item.value, index + 1);
                                                }
                                            }}
                                            disabled={item.disabled || disabled}
                                            selected={index < (selection.rating || 0)}
                                        >
                                            <RatingStar />
                                        </Styled.RatingButton>
                                    ))}
                                </Styled.RatingContainer>
                            )}
                        </Styled.ItemWrapper>
                    );
                })}
            </Styled.ItemsContainer>
            <Styled.SubmitButton>
                <Button
                    label={submitLabel}
                    value={submitLabel}
                    disabled={disabled}
                    onClick={handleSubmit}
                />
            </Styled.SubmitButton>
        </Styled.MultiSelectWrapper>
    );
};

export default MultiSelect;